import { Component, OnDestroy, OnInit } from '@angular/core';

import { select, Store } from '@ngrx/store';

import { Observable, Subscription, merge } from 'rxjs';

import { map } from 'rxjs/operators';

import { Study } from 'src/app/models/study';
import { User } from 'src/app/models/user';
import { Intervention } from 'src/app/models/intervention';
import { AppState } from 'src/app/state';
import {
  selectIsLoadingSelectedStudy,
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSelectedStudyLoaded,
} from '../../../state/study/study.selectors';
import { StudyType } from 'src/app/models/study-type';
import { environment } from 'src/environments/environment';
import { Language } from 'src/app/models/language';
import { STUDY_DESCRIPTION_IDENTIFIER, STUDY_TITLE_IDENTIFIER } from '../study-details/study-details.component';
import { Router } from '@angular/router';
import { DASHBOARD_ROUTES } from 'src/app/dashboard/dashboard.routes';
import { STUDIES_ROUTES } from '../../studies.routes';
import { selectInterventions } from 'src/app/dashboard/state/intervention/intervention.selectors';
import { PrivilegeService } from '../../../services/privilege/privilege.service';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../core/dashboard-constants';
import { selectUser } from '../../../../state/user/user.selectors';
import { Privilege } from '../../../../models/privilege';

const FINISHED_MODULES_COUNT = 'finishedModulesCount';
const MODULES_COUNT = 'modulesCount';
const PARTICIPANT_COUNT = 'participantCount';
const DEVICE_COUNT = 'deviceCount';

@Component({
  selector: 'dashboard-study-home',
  templateUrl: './study-home.component.html',
  styleUrls: ['./study-home.component.scss'],
})
export class StudyHomeComponent implements OnInit, OnDestroy {
  StudyType = StudyType;
  careerUrl = environment.careerUrl;
  appUrl = environment.appUrl;
  modulesCount = MODULES_COUNT;
  participantCount = PARTICIPANT_COUNT;
  deviceCount = DEVICE_COUNT;
  interventionTitleLimitation = 35;
  interventionDescriptionLimitation = 50;
  studyTitleIdentifier = STUDY_TITLE_IDENTIFIER;
  studyDescriptionIdentifier = STUDY_DESCRIPTION_IDENTIFIER;

  loadingSelectedStudy$: Observable<boolean>;
  selectedStudyLoaded$: Observable<boolean>;
  selectedStudy$: Observable<Study>;
  selectedStudy: Study;
  user$: Observable<User>;
  user: User;
  userHasCorrectPrivileges: Boolean;
  selectedLanguage$: Observable<Language>;

  interventions$: Observable<Intervention[]>;
  interventions: Intervention[];

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  qrdata = '';

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>, private router: Router, public privilegeService: PrivilegeService) {}

  ngOnInit(): void {
    this.userHasCorrectPrivileges = false;
    this.user$ = this.store.select(selectUser);
    this.subscriptions.add(this.user$.subscribe((user) => (this.user = user)));
    this.loadingSelectedStudy$ = this.store.select(selectIsLoadingSelectedStudy);
    this.selectedStudyLoaded$ = this.store.select(selectSelectedStudyLoaded);
    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.subscriptions.add(this.selectedStudy$.subscribe((study) => (this.selectedStudy = study)));
    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.interventions$ = this.store.select(selectInterventions);
    this.subscriptions.add(
      this.interventions$.subscribe((interventions) => {
        this.interventions = interventions;
      })
    );
  }

  getUrls(study: Study): { label: string; url: string }[] {
    if (study.type === StudyType.CC) {
      const urls = [];
      this.interventions.forEach((intervention: Intervention) => {
        urls.push({
          label: 'Url to ' + intervention.name,
          url: this.careerUrl + '/#/' + intervention.hash,
          intervention,
        });
      });
      return urls;
    }
    return [{ label: 'Enrolment URL', url: this.appUrl + '/enrol/' + study.hash }];
  }

  public getStudyHash(study: Study) {
    return study.hash;
  }

  getInterventionProgress(intervention: Intervention): number {
    let finishedModulesCount: number;
    let modulesCount: number;
    let participantCount: number;

    finishedModulesCount = this.getStatistic(intervention, FINISHED_MODULES_COUNT);
    modulesCount = this.getStatistic(intervention, MODULES_COUNT);
    participantCount = this.getStatistic(intervention, PARTICIPANT_COUNT);

    const nrModules = participantCount * modulesCount;

    return nrModules > 0 ? Math.min(100, Math.round((finishedModulesCount * 100) / nrModules)) : 0;
  }

  getStatistic(intervention: Intervention, countVariable: string): number {
    return intervention[countVariable] ? intervention[countVariable].aggregate : 0;
  }

  createFirstIntervention() {
    this.router.navigate([`/${DASHBOARD_ROUTES.STUDIES}/${this.selectedStudy.id}/${STUDIES_ROUTES.INTERVENTIONS}`]);
  }

  saveAsImage(qrcode) {
    const img = document.getElementById(qrcode).firstChild.firstChild;
    const imageData = this.getBase64Image(img);

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // IE
      window.navigator.msSaveOrOpenBlob(imageData, 'Qrcode');
    } else {
      // chrome
      const link = document.createElement('a');
      link.href = imageData;
      link.download = 'Qrcode';
      link.click();
    }
  }

  checkIfUserHasCorrectPrivileges() {
    if (this.user?.superuser) {
      return;
    }

    for (const privilege of this.user.privileges) {
      if (privilege.studyId === this.selectedStudy?.id) {
        return;
      }
    }
  }

  getBase64Image(img) {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const dataURL = canvas.toDataURL('image/jpg');

    return dataURL;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
